import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Users, TrendingUp, Clock, Globe, ArrowRight } from 'lucide-react';
import { api, LeadGenDashboardData } from '../services/api';
import { useClient } from '../contexts/ClientContext';

const LeadGenDashboard: React.FC = () => {
  const { selectedPropertyId } = useClient();
  const [data, setData] = useState<LeadGenDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, [selectedPropertyId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const result = await api.getLeadGenDashboard(selectedPropertyId || undefined);
      setData(result);
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return <div className="p-6 text-red-500">{error || 'No data'}</div>;
  }

  const statusColors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    contacted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    qualified: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    quoted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    won: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    lost: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lead Generation</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Overview of leads from owned properties</p>
        </div>
        <Link
          to="/leadgen/leads"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600 transition text-sm font-medium"
        >
          View All Leads <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Leads', value: data.total_leads, icon: Users, color: 'text-blue-500' },
          { label: 'Today', value: data.leads_today, icon: Clock, color: 'text-green-500' },
          { label: 'This Week', value: data.leads_this_week, icon: TrendingUp, color: 'text-purple-500' },
          { label: 'This Month', value: data.leads_this_month, icon: BarChart3, color: 'text-orange-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</span>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Status */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Status</h2>
          <div className="space-y-3">
            {Object.entries(data.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
            {Object.keys(data.by_status).length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">No leads yet</p>
            )}
          </div>
        </div>

        {/* By Property */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Property</h2>
          <div className="space-y-3">
            {data.by_property.map((prop) => (
              <div key={prop.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{prop.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{prop.domain} · {prop.vertical}</p>
                  </div>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white">{prop.count}</span>
              </div>
            ))}
            {data.by_property.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">No properties yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Leads</h2>
          <Link to="/leadgen/leads" className="text-sm text-primary-500 hover:text-primary-600">View all →</Link>
        </div>
        {data.recent_leads.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No leads yet. Leads will appear here as they come in.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Name</th>
                  <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Phone</th>
                  <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Service</th>
                  <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Property</th>
                  <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                  <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-2 px-3 text-gray-900 dark:text-white font-medium">{lead.name}</td>
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{lead.phone}</td>
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{lead.service_requested || '—'}</td>
                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{lead.property_name}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusColors[lead.status] || 'bg-gray-100 text-gray-800'}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadGenDashboard;
